import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { H4 } from '@blueprintjs/core';
import { Grid } from 'components/base';
import { capitalizeString, formatDate, parseDate } from 'utils/format';

import generateEventsData from '../../generator/generateEventsData';
import useEditorItemContext from '../../context/editorItemContext';

import DateSelect from '../../components/ancestry/numberOfGenerationsSelect';
import EditorLayout from '../components/layout';
import { Button, Panel, Typography } from '../../components/base';
import useEditorContext from '../../context/editorContext';

const START_DAYS = ['monday', 'sunday'];

/**
 * Get next nearest January.
 * @param {Date} date
 * @returns {Date}
 */
const nearestJanuary = date => {
  const currentYear = date.getFullYear();
  const currentMonth = date.getMonth();

  if (currentMonth === 0) {
    return new Date(currentYear, currentMonth);
  }
  return new Date(currentYear + 1, 0);
};

export const CalendarSource = props => {
  const editorContext = useEditorContext();
  const { item, template } = useEditorItemContext();

  const startMonths = useMemo(() => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();

    return [0, 1, 2].reduce((arr, year) => {
      for (let month = 0; month < 12; month += 1) {
        if (!(year === 0 && month < currentMonth) && !(year === 2 && month > 0)) {
          arr.push(formatDate(new Date(currentYear + year, month), 'YYYY-MM'));
        }
      }
      return arr;
    }, []);
  }, []);

  const [initialMonth, setInitialMonth] = useState(
    item.additionalInfo?.initialMonth ?? formatDate(nearestJanuary(new Date()), 'YYYY-MM'),
  );
  const [initialDay, setInitialDay] = useState(item.additionalInfo?.initialDay ?? START_DAYS[0]);

  const handleOnPreviewClick = async e => {
    e.preventDefault();

    props.onSuccess(() =>
      generateEventsData(editorContext.accessToken, template, [], initialMonth, initialDay, props.source),
    );
  };

  const isPreviewEnabled = !!initialMonth && !!initialDay;

  return (
    <EditorLayout
      actionButton={
        <Button disabled={!isPreviewEnabled} variant="primary" onClick={handleOnPreviewClick}>
          Preview
        </Button>
      }
      primaryChildren={props.children}
    >
      <div className="mw-1280 mx-auto">
        <Panel borderColor="green">
          <H4>Select a starting month for your calendar</H4>
          <Grid.Row>
            <Grid.Col xs={12} sm={4} className="mw-300 d-flex align-items-center">
              <DateSelect
                fill
                selectedValue={initialMonth}
                onChange={setInitialMonth}
                values={startMonths}
                mask={value => formatDate(parseDate(value, 'YYYY-MM'), 'MMMM YYYY')}
              />
            </Grid.Col>
            <Grid.Col xs={12} sm={8} className="d-flex align-items-center">
              <Typography.Paragraph mb={0}>
                This cannot be changed after you begin designing your calendar.
              </Typography.Paragraph>
            </Grid.Col>
          </Grid.Row>

          <Grid.Row className="mt-1">
            <Grid.Col xs={12} sm={4} className="mw-300 d-flex align-items-center">
              <DateSelect
                fill
                selectedValue={initialDay}
                onChange={setInitialDay}
                values={START_DAYS}
                mask={capitalizeString}
              />
            </Grid.Col>
            <Grid.Col xs={12} sm={8} className="d-flex align-items-center">
              <Typography.Paragraph mb={0}>
                Day which is the Beginning of the Week
              </Typography.Paragraph>
            </Grid.Col>
          </Grid.Row>

          {isPreviewEnabled && (
            <div className="text-right">
              <Button
                large
                variant="primary"
                disabled={!isPreviewEnabled}
                onClick={handleOnPreviewClick}
              >
                Preview your creation
              </Button>
            </div>
          )}
        </Panel>
      </div>
    </EditorLayout>
  );
};

CalendarSource.propTypes = {
  source: PropTypes.string,
  onSuccess: PropTypes.func,
  children: PropTypes.node,
};

export const CalendarSourceAncestry = props => <CalendarSource source="ancestry" {...props} />;
export const CalendarSourceFamilySearch = props => <CalendarSource source="familysearch" {...props} />;
export const CalendarSourceMyCanvas = props => <CalendarSource source="mycanvas" {...props} />;
